import {
  createContext,
  lazy,
  Suspense,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import type { TView } from '@shared/types/view';

const EditViewDialog = lazy(() =>
  import('./edit-view-dialog').then((mod) => ({ default: mod.EditViewDialog }))
);

type EditViewDialogContextValue = {
  openEditView: (view: TView) => void;
};

const EditViewDialogContext = createContext<EditViewDialogContextValue | null>(
  null
);

export function useEditViewDialog() {
  const ctx = useContext(EditViewDialogContext);
  if (!ctx) {
    throw new Error(
      'useEditViewDialog must be used within EditViewDialogHost'
    );
  }
  return ctx;
}

/** Renders EditViewDialog outside nested filter/saved-views dialogs so filter panels don't dismiss it. */
export function EditViewDialogHost({ children }: { children: ReactNode }) {
  const [editingView, setEditingView] = useState<TView | null>(null);

  const openEditView = useCallback((view: TView) => {
    setEditingView(view);
  }, []);

  const value = useMemo(() => ({ openEditView }), [openEditView]);

  return (
    <EditViewDialogContext.Provider value={value}>
      {children}
      {editingView ? (
        <Suspense fallback={null}>
          <EditViewDialog
            view={editingView}
            open
            onOpenChange={(open) => {
              if (!open) setEditingView(null);
            }}
          />
        </Suspense>
      ) : null}
    </EditViewDialogContext.Provider>
  );
}
